import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR } from "@/components/ChefCard";
import { payForBooking, fmtLocalDate } from "@/lib/payments";
import { ArrowRight, Info } from "lucide-react";

const CITIES = ["Mumbai", "Bengaluru", "Indore", "Chennai", "Goa", "Dubai"];

export default function TrialDialog({ chef, open, onOpenChange, onBooked }) {
  const [date, setDate] = useState(null);
  const [city, setCity] = useState(chef?.cities?.[0] || "Mumbai");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const fee = chef?.trial_fee || (chef?.salary_type === "international" ? 5000 : 2500);
  const gst = Math.round(fee * 0.18);
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 2);

  const submit = async () => {
    if (!date) return toast.error("Pick a trial date");
    setSaving(true);
    let booking = null;
    try {
      const r = await api.post("/bookings/trial", {
        chef_id: chef.id,
        city,
        trial_date: fmtLocalDate(date),
        contact_name: contactName,
        contact_phone: contactPhone,
        notes,
      });
      booking = r.data.booking;
      await payForBooking(booking.id);
      onBooked?.(booking);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (booking) {
        toast.error("Trial saved to My Bookings — payment didn't go through. Retry it there.");
        onBooked?.(booking);
        onOpenChange(false);
      } else {
        toast.error(msg || "Could not book the trial");
      }
    }
    finally { setSaving(false); }
  };

  if (!chef) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#0F0F0F] border-white/10 rounded-none p-0 max-h-[92vh] overflow-y-auto" data-testid="trial-dialog">
        <div className="p-8">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl">Book a paid trial</DialogTitle>
            <p className="text-neutral-400 text-sm mt-1">
              A full day in your kitchen with {chef.name} before you commit to an engagement.
            </p>
          </DialogHeader>

          <div className="mt-5 border border-[#F5B041]/30 bg-[#F5B041]/5 p-4 flex gap-3">
            <Info size={16} className="text-[#F5B041] shrink-0 mt-0.5" />
            <p className="text-xs text-neutral-300 leading-relaxed">
              Trials must be booked at least <span className="text-[#F5B041]">2 days in advance</span> and are payable
              upfront. The trial fee is non-refundable and is not adjusted against the placement fee.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-6">
            <div>
              <Label className="small-caps text-[10px] text-neutral-500">Trial date</Label>
              <div className="mt-2 border border-white/10 p-3 bg-[#141414]">
                <Calendar mode="single" selected={date} onSelect={setDate}
                  disabled={{ before: minDate }} data-testid="trial-date-picker" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Your name</Label>
                  <input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="trial-contact-name"
                    placeholder="Full name"
                    className="mt-2 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm focus:border-[#C04000] outline-none" />
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Contact number</Label>
                  <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="trial-contact-phone"
                    placeholder="+91 98765 43210"
                    className="mt-2 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm focus:border-[#C04000] outline-none" />
                </div>
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">City</Label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="trial-city" className="mt-2 rounded-none bg-transparent border-white/10 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10">
                    {CITIES.map((c) => <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">What should the chef cook?</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="trial-notes"
                  placeholder="Courses to taste, guest count, dietary notes, timings…"
                  className="mt-2 rounded-none bg-transparent border-white/10 min-h-24" />
              </div>

              <div className="bg-[#1A1A1A] border border-white/10 p-5" data-testid="trial-fee-card">
                <div className="small-caps text-[10px] text-neutral-400">Trial fee</div>
                <div className="dotted-sep my-4" />
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-neutral-400">Day rate</span><span>₹{formatINR(fee)}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">GST (18%)</span><span>₹{formatINR(gst)}</span></div>
                </div>
                <div className="dotted-sep my-4" />
                <div className="flex items-end justify-between">
                  <span className="small-caps text-[10px] text-neutral-500">Payable now</span>
                  <span className="font-serif-display text-3xl text-[#F5B041]" data-testid="trial-total">₹{formatINR(fee + gst)}</span>
                </div>
              </div>

              <Button onClick={submit} disabled={saving || !date} data-testid="trial-submit-btn"
                className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px]">
                {saving ? "Booking…" : "Confirm trial & pay"} <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
